import { Dialog, DialogTitle, DialogContent, DialogActions, FormControl, FormControlLabel, FormLabel, Radio, RadioGroup } from "@mui/material";
import { DatePicker } from "@mui/x-date-pickers/DatePicker";
import { Controller, useForm } from "react-hook-form";
import { useCallback, useEffect } from "react";
import { zodResolver } from "@hookform/resolvers/zod";
import { ModalLoading } from "../../../../../../common/components/modal_loading";

import FormContainer from "../../../../../../common/components/form_container";
import PrimaryButton from "../../../../../../common/components/primary_button";
import DefaultFormInput from "../../../../../../common/components/default_form_input";
import dayjs from "dayjs";
import { FuncionarioTreinamento } from "../../../../../rh/domain/models/funcionario_treinamento";
import { AvaliarTreinamentoForm, AvaliarTreinamentoSchema } from "../../../../../rh/presentation/schemas/Funcionarios_cadastro/avaliar_treinamento_schema";
import { LogTipoTabela } from "../../../../../../common/utils/log_tabela_tipo";
import { isNotNullOrUndefined } from "../../../../../../common/utils/utils";


type Props = {
  open: boolean,
  saving: boolean,
  selectedTreinamento: FuncionarioTreinamento | null,
  onSubmit: (form: AvaliarTreinamentoForm) => void,
  onOpenChangeHistory: (id: number, tabelaTipo: LogTipoTabela) => void,
  onClose: () => void,
};

export const AvaliarTreinamentoModal = ({
  open,
  saving,
  selectedTreinamento,
  onOpenChangeHistory,
  onSubmit,
  onClose,
}: Props) => {
  const hookForm = useForm<AvaliarTreinamentoForm>({
    mode: "onSubmit",
    reValidateMode: "onSubmit",
    resolver: zodResolver(AvaliarTreinamentoSchema),
  });

  useEffect(() => {
    hookForm.reset();
  }, [
    hookForm,
    selectedTreinamento,
  ]);

  const handleClose = useCallback(() => {
    if (!saving) onClose();
  }, [
    saving,
    onClose,
  ]);

  const handleSubmit = useCallback((form: AvaliarTreinamentoForm) => {
    if (!saving) onSubmit(form);
  }, [
    saving,
    onSubmit,
  ]);
  const handleChangeHistory = useCallback(() => {
    if (selectedTreinamento) {
      onOpenChangeHistory(selectedTreinamento.idFuncionarioTreinamento, LogTipoTabela.FUNCIONARIO_TERC_TREINAMENTO);
    }
  }, [selectedTreinamento, onOpenChangeHistory]);

  return (
    <Dialog
      open={open}
      slotProps={{
        paper: {
          component: 'form',
          onSubmit: hookForm.handleSubmit(handleSubmit),
        }
      }}
      sx={{
        "& .MuiDialog-container": {
          "& .MuiPaper-root": {
            width: "100%",
            maxWidth: "35rem",
          },
        },
      }}>
      <DialogTitle>
        Avaliar treinamento
      </DialogTitle>

      <DialogContent>
        <ModalLoading loading={saving}>
          <FormContainer title={"Informações"} arrangement="fullRow">
            <>
              <Controller
                name='eficaz'
                control={hookForm.control}
                render={({ field }) => {
                  return (
                    <FormControl size="small" className="grid-col-1">
                      <FormLabel id="eficaz">
                        O treinamento foi eficaz?
                      </FormLabel>
                      <RadioGroup
                        aria-labelledby="eficaz"
                        name="eficaz"
                        value={field.value ?? 1}
                        onChange={e => { if (!saving) field.onChange(e); }}
                        row={true}>

                        <FormControlLabel
                          className="text-black dark:text-white"
                          value={1}
                          control={<Radio />}
                          label="Sim" />

                        <FormControlLabel
                          className="text-black dark:text-white"
                          value={0}
                          control={<Radio />}
                          label="Não" />

                      </RadioGroup>
                    </FormControl>
                  );
                }} />

              <Controller
                name='dataAvaliacao'
                control={hookForm.control}
                render={({ field }) => (
                  <DatePicker
                    readOnly={saving}
                    slotProps={{ textField: { size: 'small', fullWidth: true } }}
                    label='Data da Avaliação'
                    value={field.value ? dayjs.utc(field.value as any).local().startOf('day') : null}
                    onChange={value => {
                      if (value === null || value.isValid()) field.onChange(value?.startOf('day').toDate() ?? null);
                    }} />
                )} />

              <DefaultFormInput
                name={'avaliador'}
                label={"Avaliador"}
                readOnly={saving}
                reactHookForm={hookForm}
              />

              <DefaultFormInput
                name={'evidenciaObjetiva'}
                label={"Evidência Objetiva"}
                multiline={true}
                rows={7}
                readOnly={saving}
                reactHookForm={hookForm}
              />
            </>
          </FormContainer>
        </ModalLoading>
      </DialogContent>

      <DialogActions>
        <div className="w-full h-full items-center justify-end flex p-4 gap-4">
          <div>
            {
              isNotNullOrUndefined(selectedTreinamento) &&
              <PrimaryButton
                buttonColor="yellow"
                onClick={handleChangeHistory}>
                <span className="text-xs">
                  Historico de alterações
                </span>
              </PrimaryButton>
            }
          </div>
          <div className="flex flex-row gap-2">
            <div className="p-2 hover:bg-hover-light-gray cursor-pointer dark:hover:bg-border-dark-gray rounded-[4px] font-semibold">
              <h1
                className="text-sm text-black dark:text-white"
                onClick={handleClose}>
                Cancelar
              </h1>
            </div>

            <div className="w-[120px]">
              {saving ? (
                <PrimaryButton
                  buttonColor="primary-green">
                  Salvando...
                </PrimaryButton>
              ) : (
                <PrimaryButton
                  type="submit"
                  buttonColor="primary-green">
                  Salvar
                </PrimaryButton>
              )}
            </div>
          </div>
        </div>
      </DialogActions>
    </Dialog>
  );
}
